import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import AuthShell, { GoogleButton, darkInput, darkLabel } from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "", terms: false });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Nom complet requis.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError("Adresse e-mail invalide.");
    if (form.password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    if (form.password !== form.confirm) return setError("Les mots de passe ne correspondent pas.");
    if (!form.terms) return setError("Vous devez accepter les conditions d'utilisation.");
    setSubmitting(true);
    try {
      const r = await api.post("/auth/register", {
        name: form.name, email: form.email, phone: form.phone,
        password: form.password, terms: form.terms,
      });
      localStorage.setItem("ag_token", r.data.access_token);
      setUser(r.data.user);
      navigate("/", { state: { welcome: true }, replace: true });
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Créer un compte"
      subtitle="Rejoignez votre espace de gestion automobile."
      footer={<>Déjà inscrit ? <Link to="/login" className="font-semibold text-cyan-400 hover:text-cyan-300" data-testid="goto-login-link">Se connecter</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className={darkLabel}>Nom complet</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Votre nom"
            className={darkInput} data-testid="register-name-input" />
        </div>
        <div>
          <Label className={darkLabel}>Adresse e-mail</Label>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="vous@entreprise.com"
            className={darkInput} data-testid="register-email-input" />
        </div>
        <div>
          <Label className={darkLabel}>Téléphone</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+226 …"
            className={darkInput} data-testid="register-phone-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className={darkLabel}>Mot de passe</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="8 caractères min."
              className={darkInput} data-testid="register-password-input" />
          </div>
          <div>
            <Label className={darkLabel}>Confirmation</Label>
            <Input type="password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} placeholder="••••••••"
              className={darkInput} data-testid="register-confirm-input" />
          </div>
        </div>
        <label className="flex cursor-pointer items-start gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={form.terms} onChange={(e) => set("terms", e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 accent-cyan-400" data-testid="register-terms-checkbox" />
          <span>J'accepte les <span className="text-cyan-400 underline">conditions d'utilisation</span> et la <span className="text-cyan-400 underline">politique de confidentialité</span>.</span>
        </label>
        {error && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="register-error">{error}</p>
        )}
        <Button type="submit" disabled={submitting}
          className="w-full bg-cyan-500 font-semibold text-[#05070B] hover:bg-cyan-400 transition-colors"
          data-testid="register-submit-button">
          {submitting ? "Création…" : "Créer mon compte"}
        </Button>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="h-px flex-1 bg-white/10" /> ou <span className="h-px flex-1 bg-white/10" />
        </div>
        <GoogleButton label="S'inscrire avec Google" />
      </form>
    </AuthShell>
  );
}
